import type { SupabaseClient } from '../client'
import { SupabaseError } from '../errors'
import { unwrapList, unwrapMaybe } from './_helpers'

export type ArticleFeedbackRating = 'useful' | 'confusing'

export type ArticleFeedback = {
  id: number
  article_id: number
  project_id: number
  client_user_id: string
  rating: ArticleFeedbackRating
  comment: string | null
  created_at: string
  updated_at: string
}

const COLUMNS =
  'id, article_id, project_id, client_user_id, rating, comment, created_at, updated_at'

/**
 * Busca o feedback que o cliente atual já deu pra um artigo. Null se nunca votou.
 */
export async function getMyArticleFeedback(
  client: SupabaseClient,
  articleId: number,
  clientUserId: string,
): Promise<ArticleFeedback | null> {
  // biome-ignore lint/suspicious/noExplicitAny: article_feedback ainda nao esta nos tipos gerados
  const result = await (client as any)
    .schema('portal')
    .from('article_feedback')
    .select(COLUMNS)
    .eq('article_id', articleId)
    .eq('client_user_id', clientUserId)
    .maybeSingle()
  return unwrapMaybe(result) as ArticleFeedback | null
}

/**
 * Lista feedbacks do cliente para um conjunto de artigos (cache batch do reader).
 */
export async function listMyArticleFeedbacks(
  client: SupabaseClient,
  articleIds: number[],
  clientUserId: string,
): Promise<ArticleFeedback[]> {
  if (articleIds.length === 0) return []
  // biome-ignore lint/suspicious/noExplicitAny: article_feedback ainda nao esta nos tipos gerados
  const result = await (client as any)
    .schema('portal')
    .from('article_feedback')
    .select(COLUMNS)
    .in('article_id', articleIds)
    .eq('client_user_id', clientUserId)
  return unwrapList(result) as ArticleFeedback[]
}

export type SubmitArticleFeedbackInput = {
  articleId: number
  projectId: number
  clientUserId: string
  rating: ArticleFeedbackRating
  comment?: string | null
}

/**
 * Upsert do feedback (cliente pode trocar voto).
 */
export async function submitArticleFeedback(
  client: SupabaseClient,
  input: SubmitArticleFeedbackInput,
): Promise<ArticleFeedback> {
  const payload = {
    article_id: input.articleId,
    project_id: input.projectId,
    client_user_id: input.clientUserId,
    rating: input.rating,
    comment: input.comment ?? null,
    updated_at: new Date().toISOString(),
  }
  // biome-ignore lint/suspicious/noExplicitAny: article_feedback ainda nao esta nos tipos gerados
  const result = await (client as any)
    .schema('portal')
    .from('article_feedback')
    .upsert(payload, { onConflict: 'article_id,client_user_id' })
    .select(COLUMNS)
    .single()
  if (result.error) throw new SupabaseError(result.error.message, result.error)
  return result.data as ArticleFeedback
}
